'use client'

import { useCheckout, usePaymentMethodDiscount } from '@/lib/hooks'
import { setPaymentMethodType } from '@/lib/redux'
import { use, useState } from 'react'
import { toast } from 'sonner'

import { RefContext, type Refs } from '@/components/providers/ref-provider'
import { CardValidationInput, Input, Label, Separator, Thumbnail } from '@/components/ui'
import { CardLastFour } from '@/components/ui/icons'
import React from 'react'

interface PaymentCardVerificationProps
  extends React.ComponentPropsWithoutRef<'form'> {
  onClose: () => void
}

export const PaymentCardVerification = ({
  onClose,
}: PaymentCardVerificationProps) => {
  const { amountInputRef } = use(RefContext) as Refs
  const {
    payment_method,
    setCurrentStep,
    setLastDigits,
    setFirstDigits,
    queryParams,
  } = useCheckout()
  const { applyPaymentMethodDiscount } = usePaymentMethodDiscount()
  const [isApplyingDiscount, setIsApplyingDiscount] = useState(false)
  const isFirstFourRequired = payment_method?.bin_required === 1
  const isLastFourRequired = payment_method?.last_digits_required === 1
  const hasDiscounts = payment_method?.discount?.length > 0;

  console.log(payment_method);


  const handleSubmit = async (data: FormData) => {
    const firstFour = data.get('first-four-digits') as string
    const lastFour = data.get('last-four-digits') as string
    const payble_amount = data.get('payble_amount') as string

    if (!firstFour && isFirstFourRequired) {
      return toast.error('First four digits are required')
    } else if (
      isFirstFourRequired &&
      (firstFour.length !== 4 || Number.isNaN(Number(firstFour)) === true)
    ) {
      return toast.error('Invalid first four digits')
    }

    if ((!lastFour || lastFour === '') && isLastFourRequired) {
      return toast.error('Last four digits are required')
    } else if (
      isLastFourRequired &&
      (lastFour.length !== 4 || Number.isNaN(Number(lastFour)) === true)
    ) {
      return toast.error('Invalid last four digits')
    }

    setLastDigits(lastFour as string)
    setFirstDigits(firstFour as string)
    setPaymentMethodType('card')

    if (queryParams?.cartId && payment_method?.id) {
      setIsApplyingDiscount(true)
      try {
        await applyPaymentMethodDiscount({
          cartId: queryParams.cartId,
          paymentMethodId: payment_method.id,
          payableAmount: payble_amount ? Number(payble_amount) : null,
        })
      } catch (error) {
        console.error('Failed to apply/remove discount:', error)
        // Continue even if discount operation fails
      } finally {
        setIsApplyingDiscount(false)
      }
    }

    setCurrentStep('COLLECT_PAYMENT')
    amountInputRef?.current?.focus()
    onClose?.()
  }

  return (
    <form className="flex w-full gap-5" action={handleSubmit}>
      <div className="flex flex-grow gap-6 rounded-lg border border-gray-100 p-6 dark:border-gray-700">
        <span className="hidden h-[128px] w-[192px] shrink-0 lg:flex">
          <CardLastFour />
        </span>

        <div className="flex w-full flex-col gap-2">
          <div className="flex w-full flex-col gap-2">
            <div className="flex items-center gap-1.5">
              <Thumbnail
                size="xs"
                src={payment_method?.logo_url}
                className="rounded-md border border-gray-100 !bg-white p-0.5"
              />
              <div className="flex items-center gap-2">
                <span className="text-base font-medium">
                  {payment_method?.name}
                </span>
                {hasDiscounts && <span className='text-red-600 text-xs font-medium font-mono'>({payment_method?.discount?.[0]?.discount_type === 'percentage' ? '' : 'Rs.'}{payment_method?.discount?.[0]?.discount}{payment_method?.discount?.[0]?.discount_type === 'percentage' ? '%' : ''} Discount)</span>}
              </div>
            </div>

            <div className="flex flex-col gap-4 w-full mt-2">
              {hasDiscounts && (<React.Fragment>
                <div className="flex flex-col gap-1.5">
                  <Label className='!text-sm font-medium'>Custom Payable Amount</Label>
                  <Input placeholder={'Enter amount'} name="payble_amount" />
                </div>

                <Separator />
              </React.Fragment>)}

              <CardValidationInput
                isFirstFourRequired={isFirstFourRequired}
                isLastFourRequired={isLastFourRequired}
              />
            </div>
          </div>

          <span className="text-sm text-gray-700">
            Enter first and last 4 digits of the card
          </span>
        </div>
      </div>
    </form>
  )
}
